import type { RootState } from "@/components/state/store";

interface ParsedScormManifest {
  entryHref: string;
  launchData: string;
  masteryScore: string;
  title: string;
}

interface ScormLearnerIdentity {
  id: string;
  firstName: string;
  lastName: string;
}

type CmiData = Record<string, string>;

interface ScormRuntimeData {
  runtime_version?: string | null;
  resume?: Record<string, any> | null;
  status?: Record<string, any> | null;
  score?: Record<string, any> | null;
  progress?: Record<string, any> | null;
  time?: Record<string, any> | null;
  learner?: Record<string, any> | null;
  launch?: Record<string, any> | null;
  objectives?: Record<string, any>[];
  interactions?: Record<string, any>[];
  raw_cmi_values?: Record<string, any>;
}

interface ScoreSummary {
  value: number;
  source: "course" | "objective" | "visible" | "last_visible" | "suspend_data";
  objectiveCount?: number;
}

interface ScoreSummaryOptions {
  includeLastVisibleScore?: boolean;
  includeSuspendDataScore?: boolean;
  includeVisibleScore?: boolean;
}

interface QuizResult {
  id: string;
  location: string;
  masteryScore: number | null;
  passed: boolean;
  score: number;
  title: string;
  updatedAt: string;
}

type QuizResults = Record<string, QuizResult>;

type ScormApi = {
  LMSInitialize: (_value?: string) => string;
  LMSFinish: (_value?: string) => string;
  LMSGetValue: (key: string) => string;
  LMSSetValue: (key: string, value: string) => string;
  LMSCommit: (_value?: string) => string;
  LMSGetLastError: () => string;
  LMSGetErrorString: (errorCode?: string) => string;
  LMSGetDiagnostic: (errorCode?: string) => string;
};

type Scorm2004Api = {
  Initialize: (_value?: string) => string;
  Terminate: (_value?: string) => string;
  GetValue: (key: string) => string;
  SetValue: (key: string, value: string) => string;
  Commit: (_value?: string) => string;
  GetLastError: () => string;
  GetErrorString: (errorCode?: string) => string;
  GetDiagnostic: (errorCode?: string) => string;
};

declare global {
  interface Window {
    API?: ScormApi;
    API_1484_11?: Scorm2004Api;
    kaerScormDebug?: {
      disable: () => void;
      enable: () => void;
      getCmiData: () => CmiData;
      getQuizResults: () => QuizResults;
      printCmiData: () => void;
      printQuizResults: () => void;
    };
  }
}

const API_PREFIX = "/api/v1";
const SCORM_DEBUG_STORAGE_KEY = "kaer:scorm-debug";
const QUIZ_RESULTS_CMI_KEY = "kaer.quiz_results";
const SCORM_MANIFEST_VERSION_CMI_KEY = "kaer.scorm_manifest_version";
const SCORM_MANIFEST_VERSION_RAW_CMI_KEY = "kaer.scorm_manifest_version_raw";
const SCORM_RUNTIME_VERSION_CMI_KEY = "kaer.scorm_runtime_version";
const SCORM_RUNTIME_SAVE_DEBOUNCE_MS = 1500;

const getElementsByLocalName = (root: Document | Element, localName: string) =>
  Array.from(root.getElementsByTagNameNS("*", localName));

const getFirstTextContent = (elements: Element[]) =>
  elements
    .find((element) => element.textContent?.trim())
    ?.textContent?.trim() ?? "";

const getAttributeByLocalName = (
  element: Element | undefined,
  localName: string
) => {
  if (!element) return "";

  return (
    Array.from(element.attributes).find(
      (attribute) => attribute.localName === localName
    )?.value ?? ""
  );
};

const parseScormManifest = (manifestText: string): ParsedScormManifest => {
  const parser = new DOMParser();
  const xmlDocument = parser.parseFromString(manifestText, "application/xml");
  const parserErrors = xmlDocument.getElementsByTagName("parsererror");

  if (parserErrors.length > 0) {
    throw new Error("Invalid SCORM manifest.");
  }

  const organizationsElement = getElementsByLocalName(
    xmlDocument,
    "organizations"
  )[0];
  const defaultOrganizationId =
    organizationsElement?.getAttribute("default") ?? "";
  const organizations = getElementsByLocalName(xmlDocument, "organization");
  const selectedOrganization =
    organizations.find(
      (organization) =>
        defaultOrganizationId &&
        organization.getAttribute("identifier") === defaultOrganizationId
    ) ?? organizations[0];
  const launchItem = selectedOrganization
    ? getElementsByLocalName(selectedOrganization, "item").find((item) =>
        item.getAttribute("identifierref")
      )
    : undefined;
  const identifierRef = launchItem?.getAttribute("identifierref") ?? "";
  const resources = getElementsByLocalName(xmlDocument, "resource");
  const selectedResource =
    resources.find(
      (resource) => resource.getAttribute("identifier") === identifierRef
    ) ?? resources.find((resource) => resource.getAttribute("href"));
  const entryHref = selectedResource?.getAttribute("href") ?? "";

  if (!entryHref) {
    throw new Error("No SCORM launch resource found.");
  }

  return {
    entryHref,
    launchData: getFirstTextContent(
      getElementsByLocalName(launchItem ?? xmlDocument, "datafromlms")
    ),
    masteryScore:
      getFirstTextContent(
        getElementsByLocalName(launchItem ?? xmlDocument, "masteryscore")
      ) || getAttributeByLocalName(launchItem, "masteryscore"),
    title:
      getFirstTextContent(
        getElementsByLocalName(
          launchItem ?? selectedOrganization ?? xmlDocument,
          "title"
        )
      ) || getFirstTextContent(getElementsByLocalName(xmlDocument, "title")),
  };
};

const getApiUrlFromViewLink = (viewLink: string) => {
  if (viewLink.startsWith("http://") || viewLink.startsWith("https://")) {
    return viewLink;
  }

  if (
    viewLink.startsWith("/course/") ||
    viewLink.startsWith("/courseLanguageVariant/")
  ) {
    return `${API_PREFIX}${viewLink}`;
  }

  if (viewLink.startsWith("/")) {
    return viewLink;
  }

  return `${API_PREFIX}/${viewLink}`;
};

const splitPathSuffix = (href: string) => {
  const queryIndex = href.indexOf("?");
  const hashIndex = href.indexOf("#");
  const suffixIndex = [queryIndex, hashIndex]
    .filter((index) => index >= 0)
    .sort((a, b) => a - b)[0];

  if (suffixIndex === undefined) {
    return { path: href, suffix: "" };
  }

  return {
    path: href.slice(0, suffixIndex),
    suffix: href.slice(suffixIndex),
  };
};

const normalizeScormPath = (path: string) => {
  const parts: string[] = [];

  path
    .replace(/\\/g, "/")
    .split("/")
    .forEach((part) => {
      if (!part || part === ".") return;
      if (part === "..") {
        parts.pop();
        return;
      }
      parts.push(part);
    });

  return parts.join("/");
};

const getDirectoryName = (path: string) => {
  const normalizedPath = normalizeScormPath(path);
  const lastSlashIndex = normalizedPath.lastIndexOf("/");

  return lastSlashIndex >= 0 ? normalizedPath.slice(0, lastSlashIndex) : "";
};

const joinScormPath = (...parts: string[]) =>
  normalizeScormPath(parts.filter(Boolean).join("/"));

const encodeScormPath = (path: string) =>
  path
    .split("/")
    .filter(Boolean)
    .map((segment) => encodeURIComponent(segment))
    .join("/");

const buildScormResourceUrl = ({
  manifestFilePath,
  resourceBaseLink,
  resourceHref,
}: {
  manifestFilePath: string;
  resourceBaseLink: string;
  resourceHref: string;
}) => {
  if (
    resourceHref.startsWith("http://") ||
    resourceHref.startsWith("https://")
  ) {
    return resourceHref;
  }

  const { path, suffix } = splitPathSuffix(resourceHref);
  const resourcePath = joinScormPath(getDirectoryName(manifestFilePath), path);
  const normalizedBaseLink = getApiUrlFromViewLink(resourceBaseLink);
  const baseLinkWithSlash = normalizedBaseLink.endsWith("/")
    ? normalizedBaseLink
    : `${normalizedBaseLink}/`;

  return `${baseLinkWithSlash}${encodeScormPath(resourcePath)}${suffix}`;
};

const buildScormLaunchResourceUrl = ({
  enrolmentId,
  launchToken,
  resourcePath,
}: {
  enrolmentId: string;
  launchToken: string;
  resourcePath: string;
}) => {
  const { path, suffix } = splitPathSuffix(resourcePath);
  const normalizedResourcePath = normalizeScormPath(path);

  return `${API_PREFIX}/enrolment/${encodeURIComponent(
    enrolmentId
  )}/launch/${encodeURIComponent(launchToken)}/${encodeScormPath(
    normalizedResourcePath
  )}${suffix}`;
};

const buildScormLaunchEndpointUrl = ({
  enrolmentId,
}: {
  enrolmentId: string;
}) =>
  `${API_PREFIX}/enrolment/${encodeURIComponent(enrolmentId)}/scorm_session/`;

const getLmsOrigin = () => {
  const lmsHostname = window.location.hostname.replace(/^[^.]+/, "lms");
  const port = window.location.port ? `:${window.location.port}` : "";
  return `${window.location.protocol}//${lmsHostname}${port}`;
};

const buildScormCompletionUrl = ({
  enrolmentId,
  runtimeLocation = window.location,
}: {
  enrolmentId: string;
  runtimeLocation?: Pick<Location, "hostname" | "port" | "protocol">;
}) => {
  const portalHostname = runtimeLocation.hostname.replace(/^[^.]+/, "portal");
  const port = runtimeLocation.port ? `:${runtimeLocation.port}` : "";
  return `${runtimeLocation.protocol}//${portalHostname}${port}/kaerdemy/learner/enrolments/${encodeURIComponent(
    enrolmentId
  )}/completed`;
};

const resolveScormResourcePath = ({
  manifestFilePath,
  resourceHref,
}: {
  manifestFilePath: string;
  resourceHref: string;
}) => {
  if (/^(?:[a-z][a-z\d+.-]*:)?\/\//i.test(resourceHref)) {
    return resourceHref;
  }

  const { path, suffix } = splitPathSuffix(resourceHref);
  const resolvedPath = path.startsWith("/")
    ? normalizeScormPath(path)
    : joinScormPath(getDirectoryName(manifestFilePath), path);
  return `${resolvedPath}${suffix}`;
};

const getInitialCmiData = ({
  courseId,
  launchData,
  loginState,
  learner,
  masteryScore,
}: {
  courseId?: string;
  launchData: string;
  loginState?: RootState["login"];
  learner?: ScormLearnerIdentity;
  masteryScore: string;
}): CmiData => {
  const learnerName = [
    learner?.lastName ?? loginState?.lastname,
    learner?.firstName ?? loginState?.firstname,
  ]
    .filter(Boolean)
    .join(", ");
  const learnerId = learner?.id ?? loginState?.subjectId ?? "";

  return {
    "cmi.core._children":
      "student_id,student_name,lesson_location,credit,lesson_status,entry,score,total_time,lesson_mode,exit,session_time",
    "cmi.core.score._children": "raw,min,max",
    "cmi.student_data._children":
      "mastery_score,max_time_allowed,time_limit_action",
    "cmi.core.student_id": String(learnerId),
    "cmi.core.student_name": learnerName,
    "cmi.core.credit": "credit",
    "cmi.core.entry": "ab-initio",
    "cmi.core.lesson_mode": "normal",
    "cmi.core.lesson_status": "not attempted",
    "cmi.core.total_time": "0000:00:00.00",
    "cmi.launch_data": launchData,
    "cmi.suspend_data": "",
    "cmi.student_data.mastery_score": masteryScore,
    "cmi.learner_id": String(learnerId),
    "cmi.learner_name": learnerName,
    "cmi.credit": "credit",
    "cmi.entry": "ab-initio",
    "cmi.mode": "normal",
    "cmi.completion_status": "unknown",
    "cmi.success_status": "unknown",
    "cmi.location": "",
    "cmi.score.raw": "",
    "cmi.score.min": "",
    "cmi.score.max": "",
    "cmi.score.scaled": "",
    "cmi.scaled_passing_score": masteryScore,
    "adl.nav.request": "_none_",
    "kaer.course_id": courseId ?? "",
  };
};

const parseNumber = (value: string | undefined | null) => {
  if (typeof value !== "string" || value.trim() === "") return null;

  const numericValue = Number(value.trim().replace(",", "."));

  return Number.isFinite(numericValue) ? numericValue : null;
};

const isPercentage = (value: number | null): value is number =>
  value !== null && value >= 0 && value <= 100;

const deleteCmiKey = (data: CmiData, key: string) => {
  if (!Object.prototype.hasOwnProperty.call(data, key)) return false;

  delete data[key];
  return true;
};

const getStableHash = (value: string) => {
  let hash = 0;

  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) - hash + value.charCodeAt(index)) | 0;
  }

  return Math.abs(hash).toString(36);
};

const normalizeIdentityText = (value: string) =>
  value
    .replace(/\d+(?:[.,]\d+)?\s*%/g, "%")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const parseJsonRecord = (
  value: string | undefined | null
): Record<string, any> => {
  if (!value) return {};

  try {
    const parsedValue = JSON.parse(value);

    return parsedValue &&
      typeof parsedValue === "object" &&
      !Array.isArray(parsedValue)
      ? parsedValue
      : {};
  } catch {
    return {};
  }
};

const getQuizResults = (data: CmiData): QuizResults =>
  Object.entries(
    parseJsonRecord(data[QUIZ_RESULTS_CMI_KEY])
  ).reduce<QuizResults>((quizResults, [quizId, rawResult]) => {
    if (!rawResult || typeof rawResult !== "object") return quizResults;

    const score = parseNumber(String(rawResult.score ?? ""));
    if (!isPercentage(score)) return quizResults;

    const masteryScore = parseNumber(
      rawResult.masteryScore === null || rawResult.masteryScore === undefined
        ? null
        : String(rawResult.masteryScore)
    );

    quizResults[quizId] = {
      id: String(rawResult.id || quizId),
      location: String(rawResult.location || ""),
      masteryScore: isPercentage(masteryScore) ? masteryScore : null,
      passed: Boolean(rawResult.passed),
      score,
      title: String(rawResult.title || "Quiz"),
      updatedAt: String(rawResult.updatedAt || ""),
    };

    return quizResults;
  }, {});

const getScormRuntimeVersionFromKey = (key: string) => {
  if (
    key === "cmi.completion_status" ||
    key === "cmi.success_status" ||
    key === "cmi.progress_measure" ||
    key === "cmi.location" ||
    key === "cmi.exit" ||
    key === "cmi.session_time" ||
    key === "cmi.total_time" ||
    key === "cmi.score.raw" ||
    key === "cmi.score.min" ||
    key === "cmi.score.max" ||
    key === "cmi.score.scaled" ||
    key === "cmi.scaled_passing_score" ||
    (key.startsWith("cmi.objectives.") &&
      (key.endsWith(".completion_status") ||
        key.endsWith(".success_status") ||
        key.endsWith(".progress_measure")))
  ) {
    return "2004";
  }

  if (
    key.startsWith("cmi.core.") ||
    key.startsWith("cmi.student_data.") ||
    key.startsWith("cmi.student_preference.")
  ) {
    return "1.2";
  }

  return "";
};

const getScormRuntimeVersion = (data: CmiData) => {
  if (data[SCORM_RUNTIME_VERSION_CMI_KEY]) {
    return data[SCORM_RUNTIME_VERSION_CMI_KEY];
  }
  if (data[SCORM_MANIFEST_VERSION_CMI_KEY]) {
    return data[SCORM_MANIFEST_VERSION_CMI_KEY];
  }

  const successStatus = (data["cmi.success_status"] ?? "").toLowerCase();
  const hasScorm2004OnlyValue =
    parseNumber(data["cmi.progress_measure"]) !== null ||
    parseNumber(data["cmi.score.scaled"]) !== null ||
    parseNumber(data["cmi.scaled_passing_score"]) !== null ||
    Boolean(data["cmi.session_time"]) ||
    Boolean(data["cmi.exit"]) ||
    ["passed", "failed"].includes(successStatus);

  return hasScorm2004OnlyValue ? "2004" : "";
};

const normalizeScoreToPercent = (score: number | null, sourceKey: string) => {
  if (score === null) return null;
  if (sourceKey.includes("scaled")) {
    return score <= 1 ? score * 100 : score;
  }

  return score;
};

const normalizePassingScoreToPercent = (score: number | null) => {
  if (score === null) return null;

  return score <= 1 ? score * 100 : score;
};

const getLzwCodeList = (value: string | undefined | null) => {
  if (!value) return null;

  try {
    const parsedValue = JSON.parse(value);
    if (Array.isArray(parsedValue)) {
      const parsedCodes = parsedValue.map((item) => Number(item));

      return parsedCodes.every(Number.isFinite) ? parsedCodes : null;
    }
  } catch {
    // Compressed SCORM 1.2 suspend data is often persisted as comma-separated codes.
  }

  const codes = value
    .split(",")
    .map((item) => Number(item.trim()))
    .filter((item) => Number.isFinite(item));

  return codes.length > 0 && codes.length === value.split(",").length
    ? codes
    : null;
};

const unpackLzwCodes = (codes: number[]) => {
  if (codes.length === 0) return "";

  const dictionary = Array.from({ length: 256 }, (_value, index) =>
    String.fromCharCode(index)
  );
  let word = String.fromCharCode(codes[0]);
  let result = word;

  for (let index = 1; index < codes.length; index += 1) {
    const code = codes[index];
    const entry =
      dictionary[code] ?? (code === dictionary.length ? word + word[0] : "");

    if (!entry) return "";

    result += entry;
    dictionary.push(word + entry[0]);
    word = entry;
  }

  return result;
};

const unpackKeyOptimizedObject = (value: any): any => {
  if (!value || typeof value !== "object" || !Array.isArray(value.__k)) {
    return value;
  }

  const keys = value.__k;
  const decode = (node: any): any => {
    if (!node || typeof node !== "object") return node;

    if (Array.isArray(node)) {
      return node.map(decode);
    }

    return Object.entries(node).reduce<Record<string, any>>(
      (decodedObject, [key, childValue]) => {
        decodedObject[keys[Number(key)] ?? key] = decode(childValue);
        return decodedObject;
      },
      {}
    );
  };

  return decode(value.__v);
};

const getSuspendDataObject = (value: string | undefined | null) => {
  const codes = getLzwCodeList(value);
  if (!codes) return null;

  const unpackedValue = unpackLzwCodes(codes);
  if (!unpackedValue) return null;

  try {
    const parsedValue = JSON.parse(unpackedValue);

    return unpackKeyOptimizedObject(parsedValue);
  } catch {
    return null;
  }
};

const findNumericField = (value: any, fieldNames: string[]): number | null => {
  if (!value || typeof value !== "object") return null;

  if (!Array.isArray(value)) {
    for (const fieldName of fieldNames) {
      const numericValue = parseNumber(
        value[fieldName] === undefined ? null : String(value[fieldName])
      );
      if (numericValue !== null) return numericValue;
    }
  }

  for (const childValue of Object.values(value)) {
    const numericValue = findNumericField(childValue, fieldNames);
    if (numericValue !== null) return numericValue;
  }

  return null;
};

const getDisplayStatus = (data: CmiData) =>
  data["cmi.core.lesson_status"] ||
  data["cmi.completion_status"] ||
  data["cmi.success_status"] ||
  "not attempted";

const roundMetric = (value: number) => Math.round(value * 100) / 100;

const normalizeRawScoreToPercent = ({
  max,
  min,
  raw,
}: {
  max: number | null;
  min: number | null;
  raw: number | null;
}) => {
  if (raw === null) return null;
  if (max !== null && max > 0 && max !== 100) {
    const effectiveMin = min ?? 0;
    const range = max - effectiveMin;

    if (range > 0) {
      return ((raw - effectiveMin) / range) * 100;
    }
  }

  return raw;
};

const getNormalizedScoreFromKeys = ({
  data,
  maxKey,
  minKey,
  rawKey,
  scaledKey,
}: {
  data: CmiData;
  maxKey?: string;
  minKey?: string;
  rawKey?: string;
  scaledKey?: string;
}) => {
  const rawScore = rawKey
    ? normalizeRawScoreToPercent({
        raw: parseNumber(data[rawKey]),
        min: minKey ? parseNumber(data[minKey]) : null,
        max: maxKey ? parseNumber(data[maxKey]) : null,
      })
    : null;

  if (rawScore !== null) {
    return roundMetric(rawScore);
  }

  const scaledScore = scaledKey
    ? normalizeScoreToPercent(parseNumber(data[scaledKey]), scaledKey)
    : null;

  return scaledScore === null ? null : roundMetric(scaledScore);
};

const getObjectiveScores = (data: CmiData) => {
  const objectiveIndexes = new Set<string>();

  Object.keys(data).forEach((key) => {
    const match = key.match(/^cmi\.objectives\.(\d+)\./);
    if (match) {
      objectiveIndexes.add(match[1]);
    }
  });

  return Array.from(objectiveIndexes)
    .map((objectiveIndex) =>
      getNormalizedScoreFromKeys({
        data,
        rawKey: `cmi.objectives.${objectiveIndex}.score.raw`,
        minKey: `cmi.objectives.${objectiveIndex}.score.min`,
        maxKey: `cmi.objectives.${objectiveIndex}.score.max`,
        scaledKey: `cmi.objectives.${objectiveIndex}.score.scaled`,
      })
    )
    .filter(
      (objectiveScore): objectiveScore is number => objectiveScore !== null
    );
};

const hasAssessmentResultSignal = (data: CmiData) => {
  const lessonStatus = (data["cmi.core.lesson_status"] ?? "").toLowerCase();
  const completionStatus = (data["cmi.completion_status"] ?? "").toLowerCase();
  const successStatus = (data["cmi.success_status"] ?? "").toLowerCase();

  return (
    ["completed", "passed", "failed"].includes(lessonStatus) ||
    completionStatus === "completed" ||
    ["passed", "failed"].includes(successStatus)
  );
};

const getScoreSummary = (
  data: CmiData,
  {
    includeLastVisibleScore = true,
    includeSuspendDataScore = true,
    includeVisibleScore = true,
  }: ScoreSummaryOptions = {}
): ScoreSummary | null => {
  const courseScore =
    getNormalizedScoreFromKeys({
      data,
      rawKey: "cmi.core.score.raw",
      minKey: "cmi.core.score.min",
      maxKey: "cmi.core.score.max",
      scaledKey: "cmi.score.scaled",
    }) ??
    getNormalizedScoreFromKeys({
      data,
      rawKey: "cmi.score.raw",
      minKey: "cmi.score.min",
      maxKey: "cmi.score.max",
      scaledKey: "cmi.score.scaled",
    });

  if (courseScore !== null) {
    return {
      value: courseScore,
      source: "course",
    };
  }

  const objectiveScores = getObjectiveScores(data);
  if (objectiveScores.length > 0) {
    const objectiveAverage =
      objectiveScores.reduce((sum, objectiveScore) => sum + objectiveScore, 0) /
      objectiveScores.length;

    return {
      value: roundMetric(objectiveAverage),
      source: "objective",
      objectiveCount: objectiveScores.length,
    };
  }

  if (includeVisibleScore) {
    const visibleScore = normalizePassingScoreToPercent(
      parseNumber(data["kaer.visible_score"])
    );
    if (visibleScore !== null) {
      return {
        value: roundMetric(visibleScore),
        source: "visible",
      };
    }
  }

  if (includeLastVisibleScore) {
    const lastVisibleScore = normalizePassingScoreToPercent(
      parseNumber(data["kaer.last_visible_score"])
    );
    if (lastVisibleScore !== null) {
      return {
        value: roundMetric(lastVisibleScore),
        source: "last_visible",
      };
    }
  }

  if (!includeSuspendDataScore || !hasAssessmentResultSignal(data)) {
    return null;
  }

  const suspendDataScore = normalizePassingScoreToPercent(
    findNumericField(getSuspendDataObject(data["cmi.suspend_data"]), [
      "score",
      "rawScore",
      "scoreRaw",
      "scorePercent",
      "scaledScore",
    ])
  );

  if (suspendDataScore !== null) {
    return {
      value: roundMetric(suspendDataScore),
      source: "suspend_data",
    };
  }

  return null;
};

const getScore = (data: CmiData) => getScoreSummary(data)?.value ?? null;

const getHeaderScore = (data: CmiData) =>
  getScoreSummary(data, {
    includeLastVisibleScore: false,
    includeSuspendDataScore: false,
    includeVisibleScore: true,
  })?.value ?? null;

const getReportedMasteryScore = (data: CmiData) =>
  normalizePassingScoreToPercent(
    parseNumber(data["cmi.student_data.mastery_score"])
  ) ??
  normalizePassingScoreToPercent(
    parseNumber(data["cmi.scaled_passing_score"])
  ) ??
  normalizePassingScoreToPercent(
    findNumericField(getSuspendDataObject(data["cmi.suspend_data"]), [
      "masteryScore",
      "passingScore",
      "scaledPassingScore",
    ])
  );

const getMasteryScore = (data: CmiData) =>
  getReportedMasteryScore(data) ??
  normalizePassingScoreToPercent(
    parseNumber(data["kaer.visible_mastery_score"])
  ) ??
  normalizePassingScoreToPercent(
    parseNumber(data["kaer.last_visible_mastery_score"])
  );

const hasMetMasteryScore = (data: CmiData) => {
  const masteryScore = getMasteryScore(data);
  if (masteryScore === null) return true;

  const score = getScore(data);
  if (score === null) return false;

  return score >= masteryScore;
};

const getFailedObjectiveIds = (data: CmiData) => {
  const objectiveIndexes = new Set<string>();
  const masteryScore = getMasteryScore(data);

  Object.keys(data).forEach((key) => {
    const match = key.match(/^cmi\.objectives\.(\d+)\./);
    if (match) {
      objectiveIndexes.add(match[1]);
    }
  });

  return Array.from(objectiveIndexes).reduce<string[]>(
    (failedObjectiveIds, objectiveIndex) => {
      const rawScore = parseNumber(
        data[`cmi.objectives.${objectiveIndex}.score.raw`]
      );
      const maxScore = parseNumber(
        data[`cmi.objectives.${objectiveIndex}.score.max`]
      );
      const scaledScore = parseNumber(
        data[`cmi.objectives.${objectiveIndex}.score.scaled`]
      );
      const score = getNormalizedScoreFromKeys({
        data,
        rawKey: `cmi.objectives.${objectiveIndex}.score.raw`,
        minKey: `cmi.objectives.${objectiveIndex}.score.min`,
        maxKey: `cmi.objectives.${objectiveIndex}.score.max`,
        scaledKey: `cmi.objectives.${objectiveIndex}.score.scaled`,
      });
      const status = (
        data[`cmi.objectives.${objectiveIndex}.status`] ?? ""
      ).toLowerCase();
      const successStatus = (
        data[`cmi.objectives.${objectiveIndex}.success_status`] ?? ""
      ).toLowerCase();
      const failedByStatus = status === "failed" || successStatus === "failed";
      const canCompareScoreToMastery =
        scaledScore !== null ||
        (maxScore !== null && maxScore > 0) ||
        (rawScore !== null && rawScore > 1);
      const failedByScore =
        canCompareScoreToMastery &&
        score !== null &&
        masteryScore !== null &&
        score < masteryScore;

      if (failedByStatus || failedByScore) {
        failedObjectiveIds.push(`objective:${objectiveIndex}`);
      }

      return failedObjectiveIds;
    },
    []
  );
};

const getKnownQuizRequirementEvaluation = (data: CmiData) => {
  const quizResults = Object.values(getQuizResults(data));
  const failedVisibleQuizIds = quizResults
    .filter((quizResult) => !quizResult.passed)
    .map((quizResult) => quizResult.id);
  const failedObjectiveIds = getFailedObjectiveIds(data);
  const failedQuizIds = [...failedVisibleQuizIds, ...failedObjectiveIds];

  return {
    failedQuizIds,
    hasFailedKnownQuiz: failedQuizIds.length > 0,
    hasKnownQuizResults:
      quizResults.length > 0 || failedObjectiveIds.length > 0,
  };
};

const getCompletionEvaluation = (data: CmiData) => {
  const lessonStatus = (data["cmi.core.lesson_status"] ?? "").toLowerCase();
  const completionStatus = (data["cmi.completion_status"] ?? "").toLowerCase();
  const successStatus = (data["cmi.success_status"] ?? "").toLowerCase();
  const progress = getProgress(data);
  const score = getScore(data);
  const masteryScore = getMasteryScore(data);
  const quizRequirementEvaluation = getKnownQuizRequirementEvaluation(data);
  const scormRuntimeVersion = getScormRuntimeVersion(data);
  const hasPassingSignal =
    lessonStatus === "passed" || successStatus === "passed";
  const hasFailureSignal =
    lessonStatus === "failed" || successStatus === "failed";
  const hasCompletionSignal =
    progress === 100 ||
    lessonStatus === "completed" ||
    lessonStatus === "passed" ||
    completionStatus === "completed";
  const hasPendingSuccessRequirement =
    scormRuntimeVersion === "2004" &&
    hasCompletionSignal &&
    successStatus === "unknown" &&
    score === null &&
    masteryScore === null;
  const hasMetScoreRequirement =
    masteryScore !== null
      ? score !== null && hasMetMasteryScore(data)
      : !hasPendingSuccessRequirement && (score === null || hasPassingSignal);
  const hasFailedRequirement =
    hasFailureSignal ||
    (!hasPassingSignal && quizRequirementEvaluation.hasFailedKnownQuiz) || // the package's own passed signal outranks scraped quiz verdicts: scrapers misfire on intro screens ("Score 0%") and stale failures of other lessons must not outweigh a passed package
    (hasCompletionSignal && masteryScore !== null && !hasMetScoreRequirement) ||
    (hasCompletionSignal &&
      score !== null &&
      masteryScore === null &&
      !hasPassingSignal);

  return {
    hasCompletionSignal,
    hasFailedRequirement,
    hasMetScoreRequirement,
    hasPassingSignal,
    hasPendingSuccessRequirement,
    quizRequirementEvaluation,
    shouldComplete:
      hasCompletionSignal &&
      !hasPendingSuccessRequirement &&
      !hasFailedRequirement &&
      hasMetScoreRequirement,
  };
};

function getProgress(data: CmiData) {
  const progressMeasure = parseNumber(data["cmi.progress_measure"]);
  if (progressMeasure !== null) {
    return Math.round(
      Math.max(
        0,
        Math.min(
          100,
          progressMeasure <= 1 ? progressMeasure * 100 : progressMeasure
        )
      )
    );
  }

  const lessonStatus = (data["cmi.core.lesson_status"] ?? "").toLowerCase();
  const completionStatus = (data["cmi.completion_status"] ?? "").toLowerCase();

  if (
    ["completed", "passed"].includes(lessonStatus) ||
    completionStatus === "completed"
  ) {
    return 100;
  }

  const visibleProgress = parseNumber(data["kaer.visible_progress"]);
  if (isPercentage(visibleProgress)) {
    return Math.round(visibleProgress);
  }

  if (
    lessonStatus === "not attempted" ||
    completionStatus === "not attempted" ||
    (!lessonStatus && completionStatus === "unknown") ||
    (lessonStatus === "unknown" && completionStatus === "unknown")
  ) {
    return 0;
  }

  return null;
}

const getEnrolmentStatusFromCmiData = (data: CmiData) => {
  const { shouldComplete } = getCompletionEvaluation(data);

  return shouldComplete ? "completed" : null;
};

const getEffectiveDisplayStatus = (data: CmiData) => {
  const completionEvaluation = getCompletionEvaluation(data);
  const enrolmentStatus = getEnrolmentStatusFromCmiData(data);
  const displayStatus = getDisplayStatus(data).toLowerCase();

  if (enrolmentStatus === "completed") {
    return "passed";
  }
  if (
    completionEvaluation.hasCompletionSignal &&
    completionEvaluation.hasFailedRequirement
  ) {
    return "failed";
  }
  if (completionEvaluation.hasPendingSuccessRequirement) return "incomplete";
  if (displayStatus === "failed") return "incomplete";

  return getDisplayStatus(data);
};

const getCompletionChipTranslationKey = (status: string) => {
  const normalizedStatus = status.toLowerCase();

  if (
    ["incomplete", "browsed", "not attempted", "unknown"].includes(
      normalizedStatus
    )
  ) {
    return "in_progress";
  }

  return normalizedStatus;
};

const getStringValue = (value: any) =>
  value === null || value === undefined ? "" : String(value);

const getNullableStringValue = (value: any) => {
  const stringValue = getStringValue(value).trim();

  return stringValue || null;
};

const getAllowedValue = <T extends string>(
  value: string | undefined | null,
  allowedValues: readonly T[]
): T | null => {
  const normalizedValue = String(value ?? "").trim() as T;

  return allowedValues.includes(normalizedValue) ? normalizedValue : null;
};

const getRawCmiValuesFromRuntime = (
  runtimeData?: ScormRuntimeData | null
): CmiData =>
  Object.entries(runtimeData?.raw_cmi_values ?? {}).reduce<CmiData>(
    (rawCmiValues, [key, value]) => {
      rawCmiValues[key] = getStringValue(value);
      return rawCmiValues;
    },
    {}
  );

const getRuntimeVersion = (value: string | undefined | null) =>
  getAllowedValue(value, ["1.2", "2004", "unknown"] as const);

const getEntryValue = (value: string | undefined | null) =>
  getAllowedValue(value, ["ab-initio", "resume"] as const);

const getExitValue = (value: string | undefined | null) =>
  getAllowedValue(value, ["time-out", "suspend", "logout", "normal"] as const);

const getLessonStatusValue = (value: string | undefined | null) =>
  getAllowedValue(String(value ?? "").toLowerCase(), [
    "passed",
    "completed",
    "failed",
    "incomplete",
    "browsed",
    "not attempted",
  ] as const);

const getCompletionStatusValue = (value: string | undefined | null) =>
  getAllowedValue(String(value ?? "").toLowerCase(), [
    "completed",
    "incomplete",
    "not attempted",
    "unknown",
  ] as const);

const getSuccessStatusValue = (value: string | undefined | null) =>
  getAllowedValue(String(value ?? "").toLowerCase(), [
    "passed",
    "failed",
    "unknown",
  ] as const);

const getCreditValue = (value: string | undefined | null) =>
  getAllowedValue(value, ["credit", "no-credit"] as const);

const getModeValue = (value: string | undefined | null) =>
  getAllowedValue(value, ["browse", "normal", "review"] as const);

const getDecimalValue = (value: string | undefined | null) =>
  parseNumber(value);

export const buildCmiDataFromScormRuntime = ({
  courseData,
  initialCmiData,
  runtimeData,
}: {
  courseData?: any;
  initialCmiData: CmiData;
  runtimeData?: ScormRuntimeData | null;
}): CmiData => {
  const rawCmiValues = getRawCmiValuesFromRuntime(runtimeData);
  const runtimeVersion =
    getRuntimeVersion(
      runtimeData?.runtime_version ??
        rawCmiValues[SCORM_RUNTIME_VERSION_CMI_KEY]
    ) ??
    getRuntimeVersion(String(courseData?.scorm_version ?? "").trim()) ??
    "";
  const suspendData =
    rawCmiValues["cmi.suspend_data"] ??
    getStringValue(runtimeData?.resume?.suspend_data);
  const lessonLocation =
    rawCmiValues["cmi.core.lesson_location"] ||
    rawCmiValues["cmi.location"] ||
    getStringValue(runtimeData?.resume?.lesson_location);
  const cmiData: CmiData = {
    ...initialCmiData,
    ...rawCmiValues,
    [SCORM_MANIFEST_VERSION_CMI_KEY]: String(
      courseData?.scorm_version ?? ""
    ).trim(),
    [SCORM_MANIFEST_VERSION_RAW_CMI_KEY]: String(
      courseData?.scorm_version_raw ?? ""
    ).trim(),
  };

  if (runtimeVersion) {
    cmiData[SCORM_RUNTIME_VERSION_CMI_KEY] = runtimeVersion;
  }
  if (suspendData) {
    cmiData["cmi.suspend_data"] = suspendData;
  }
  if (lessonLocation) {
    cmiData["cmi.core.lesson_location"] = lessonLocation;
    cmiData["cmi.location"] = lessonLocation;
  }

  const status = runtimeData?.status ?? {};
  const score = runtimeData?.score ?? {};
  const progress = runtimeData?.progress ?? {};
  const time = runtimeData?.time ?? {};
  const learner = runtimeData?.learner ?? {};
  const launch = runtimeData?.launch ?? {};
  const setRuntimeCmiValue = (key: string, value: any) => {
    if (Object.prototype.hasOwnProperty.call(rawCmiValues, key)) return;

    const stringValue = getStringValue(value);
    if (stringValue) {
      cmiData[key] = stringValue;
    }
  };

  setRuntimeCmiValue("cmi.core.lesson_status", status.lesson_status);
  setRuntimeCmiValue("cmi.completion_status", status.completion_status);
  setRuntimeCmiValue("cmi.success_status", status.success_status);
  setRuntimeCmiValue("cmi.core.score.raw", score.raw);
  setRuntimeCmiValue("cmi.score.raw", score.raw);
  setRuntimeCmiValue("cmi.core.score.min", score.min);
  setRuntimeCmiValue("cmi.score.min", score.min);
  setRuntimeCmiValue("cmi.core.score.max", score.max);
  setRuntimeCmiValue("cmi.score.max", score.max);
  setRuntimeCmiValue("cmi.score.scaled", score.scaled);
  setRuntimeCmiValue("cmi.student_data.mastery_score", score.mastery_score);
  setRuntimeCmiValue("cmi.scaled_passing_score", score.scaled_passing_score);
  setRuntimeCmiValue("cmi.progress_measure", progress.progress_measure);
  setRuntimeCmiValue("cmi.core.session_time", time.session_time);
  setRuntimeCmiValue("cmi.session_time", time.session_time);
  setRuntimeCmiValue("cmi.core.total_time", time.total_time);
  setRuntimeCmiValue("cmi.total_time", time.total_time);
  setRuntimeCmiValue("cmi.core.student_id", learner.learner_id);
  setRuntimeCmiValue("cmi.learner_id", learner.learner_id);
  setRuntimeCmiValue("cmi.core.student_name", learner.learner_name);
  setRuntimeCmiValue("cmi.learner_name", learner.learner_name);
  setRuntimeCmiValue("cmi.core.credit", learner.credit);
  setRuntimeCmiValue("cmi.credit", learner.credit);
  setRuntimeCmiValue("cmi.core.lesson_mode", learner.mode);
  setRuntimeCmiValue("cmi.mode", learner.mode);
  setRuntimeCmiValue("cmi.launch_data", launch.launch_data);

  if (cmiData["cmi.suspend_data"]) {
    cmiData["cmi.core.entry"] = "resume";
    cmiData["cmi.entry"] = "resume";
  }

  return cmiData;
};

const getSortedCmiIndexes = (data: CmiData, pattern: RegExp) => {
  const indexes = new Set<number>();

  Object.keys(data).forEach((key) => {
    const match = key.match(pattern);
    if (!match) return;

    const index = Number(match[1]);
    if (Number.isInteger(index)) {
      indexes.add(index);
    }
  });

  return Array.from(indexes).sort((a, b) => a - b);
};

export const buildObjectivesFromCmiData = (data: CmiData) =>
  getSortedCmiIndexes(data, /^cmi\.objectives\.(\d+)\./).map((index) => ({
    index,
    objective_id: getNullableStringValue(data[`cmi.objectives.${index}.id`]),
    score_raw: getDecimalValue(data[`cmi.objectives.${index}.score.raw`]),
    score_min: getDecimalValue(data[`cmi.objectives.${index}.score.min`]),
    score_max: getDecimalValue(data[`cmi.objectives.${index}.score.max`]),
    score_scaled: getDecimalValue(data[`cmi.objectives.${index}.score.scaled`]),
    status: getNullableStringValue(data[`cmi.objectives.${index}.status`]),
    completion_status: getNullableStringValue(
      data[`cmi.objectives.${index}.completion_status`]
    ),
    success_status: getNullableStringValue(
      data[`cmi.objectives.${index}.success_status`]
    ),
    progress_measure: getDecimalValue(
      data[`cmi.objectives.${index}.progress_measure`]
    ),
  }));

export const buildInteractionsFromCmiData = (data: CmiData) =>
  getSortedCmiIndexes(data, /^cmi\.interactions\.(\d+)\./).map((index) => {
    const correctResponseIndexes = getSortedCmiIndexes(
      data,
      new RegExp(
        `^cmi\\.interactions\\.${index}\\.correct_responses\\.(\\d+)\\.`
      )
    );

    return {
      index,
      interaction_id: getNullableStringValue(
        data[`cmi.interactions.${index}.id`]
      ),
      type: getNullableStringValue(data[`cmi.interactions.${index}.type`]),
      learner_response:
        data[`cmi.interactions.${index}.learner_response`] ??
        data[`cmi.interactions.${index}.student_response`] ??
        "",
      correct_responses: correctResponseIndexes
        .map((responseIndex) =>
          getStringValue(
            data[
              `cmi.interactions.${index}.correct_responses.${responseIndex}.pattern`
            ]
          )
        )
        .filter(Boolean),
      result: getNullableStringValue(data[`cmi.interactions.${index}.result`]),
      timestamp: getNullableStringValue(
        data[`cmi.interactions.${index}.timestamp`] ??
          data[`cmi.interactions.${index}.time`]
      ),
      latency: getNullableStringValue(
        data[`cmi.interactions.${index}.latency`]
      ),
      description:
        data[`cmi.interactions.${index}.description`] ??
        data[`cmi.interactions.${index}.objective.description`] ??
        "",
    };
  });

export const buildRawCmiValuesForBackend = (data: CmiData) =>
  Object.entries(data).reduce<CmiData>((rawCmiValues, [key, value]) => {
    const stringValue = getStringValue(value);
    if (stringValue !== "") {
      rawCmiValues[key] = stringValue;
    }

    return rawCmiValues;
  }, {});

export const buildScormRuntimePayload = (data: CmiData) => {
  const runtimeVersion =
    getRuntimeVersion(getScormRuntimeVersion(data)) ??
    getRuntimeVersion(data[SCORM_MANIFEST_VERSION_CMI_KEY]) ??
    "unknown";
  const completionEvaluation = getCompletionEvaluation(data);
  const reportedLessonStatus = getLessonStatusValue(
    data["cmi.core.lesson_status"]
  );
  const lessonStatus = completionEvaluation.shouldComplete
    ? reportedLessonStatus === "passed"
      ? "passed"
      : "completed"
    : completionEvaluation.hasFailedRequirement ||
      completionEvaluation.hasPendingSuccessRequirement
    ? "incomplete"
    : reportedLessonStatus ?? "incomplete";
  const progressMeasure = getDecimalValue(data["cmi.progress_measure"]);
  const progressPercentage = getProgress(data);

  return {
    runtime_version: runtimeVersion,
    resume: {
      suspend_data: data["cmi.suspend_data"] ?? "",
      lesson_location:
        data["cmi.core.lesson_location"] || data["cmi.location"] || "",
      entry: getEntryValue(data["cmi.core.entry"] || data["cmi.entry"]),
      exit: getExitValue(data["cmi.core.exit"] || data["cmi.exit"]),
    },
    status: {
      lesson_status: lessonStatus,
      completion_status:
        getCompletionStatusValue(data["cmi.completion_status"]) ??
        (completionEvaluation.shouldComplete ? "completed" : "unknown"),
      success_status:
        getSuccessStatusValue(data["cmi.success_status"]) ?? "unknown",
    },
    score: {
      raw: getDecimalValue(data["cmi.core.score.raw"] || data["cmi.score.raw"]),
      min: getDecimalValue(data["cmi.core.score.min"] || data["cmi.score.min"]),
      max: getDecimalValue(data["cmi.core.score.max"] || data["cmi.score.max"]),
      scaled: getDecimalValue(data["cmi.score.scaled"]),
      percentage: getScore(data),
      mastery_score: getMasteryScore(data),
      scaled_passing_score: getDecimalValue(data["cmi.scaled_passing_score"]),
    },
    progress: {
      progress_measure:
        progressMeasure === null && progressPercentage === 100
          ? 1
          : progressMeasure,
      percentage: progressPercentage,
    },
    time: {
      session_time:
        data["cmi.core.session_time"] || data["cmi.session_time"] || "",
      total_time: data["cmi.core.total_time"] || data["cmi.total_time"] || "",
      total_time_seconds: null,
    },
    learner: {
      learner_id: data["cmi.core.student_id"] || data["cmi.learner_id"] || "",
      learner_name:
        data["cmi.core.student_name"] || data["cmi.learner_name"] || "",
      credit:
        getCreditValue(data["cmi.core.credit"] || data["cmi.credit"]) ??
        "credit",
      mode:
        getModeValue(data["cmi.core.lesson_mode"] || data["cmi.mode"]) ??
        "normal",
    },
    launch: {
      launch_data: data["cmi.launch_data"] ?? "",
    },
    objectives: buildObjectivesFromCmiData(data),
    interactions: buildInteractionsFromCmiData(data),
    raw_cmi_values: buildRawCmiValuesForBackend(data),
    last_committed_on: new Date().toISOString(),
  };
};

export {
  buildScormCompletionUrl,
  buildScormLaunchEndpointUrl,
  buildScormLaunchResourceUrl,
  buildScormResourceUrl,
  deleteCmiKey,
  getLmsOrigin,
  getApiUrlFromViewLink,
  getCompletionChipTranslationKey,
  getEffectiveDisplayStatus,
  getHeaderScore,
  getInitialCmiData,
  getProgress,
  getQuizResults,
  getReportedMasteryScore,
  getScormRuntimeVersionFromKey,
  getStableHash,
  isPercentage,
  normalizeIdentityText,
  parseNumber,
  parseScormManifest,
  QUIZ_RESULTS_CMI_KEY,
  roundMetric,
  resolveScormResourcePath,
  SCORM_DEBUG_STORAGE_KEY,
  SCORM_RUNTIME_VERSION_CMI_KEY,
  SCORM_RUNTIME_SAVE_DEBOUNCE_MS,
};

export type {
  CmiData,
  ParsedScormManifest,
  QuizResult,
  QuizResults,
  ScormLearnerIdentity,
  ScormRuntimeData,
};
